import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { toast } from "react-toastify"
import type { ITdsReceivableSliceState, ITdsReceivableQueryParams } from "../../utils/tdsReceivable.types"
import { ApiStatus } from "../../utils/api.types"
import { fetchTdsReceivable, exportTdsReceivable } from "./tdsReceivableThunk"

/**
 * TDS Receivable slice initial state.
 */
const initialState: ITdsReceivableSliceState = {
  invoices: [],
  summary: null,
  total: 0,
  status: ApiStatus.IDLE,
  errorMessage: "",
  currentPage: 1,
  limit: 12,
  filters: {},
}

/**
 * TDS Receivable Slice.
 */
const tdsReceivableSlice = createSlice({
  name: "tdsReceivable",
  initialState: initialState,
  reducers: {
    // Set filters
    setFilters: (state, action: PayloadAction<ITdsReceivableQueryParams>) => {
      state.filters = { ...state.filters, ...action.payload }
    },

    // Clear filters
    clearFilters: (state) => {
      state.filters = {}
      state.currentPage = 1
    },

    // Set current page
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload
    },

    // Set limit
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload
      state.currentPage = 1 // Reset to first page when limit changes
    },

    // Reset state
    resetState: (state) => {
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch TDS Receivable
      .addCase(fetchTdsReceivable.pending, (state) => {
        state.status = ApiStatus.LOADING
        state.errorMessage = ""
      })
      .addCase(fetchTdsReceivable.fulfilled, (state, action) => {
        state.status = ApiStatus.SUCCESS
        state.invoices = action.payload.invoices || []
        state.summary = action.payload.summary || null
        state.total = action.payload.total || 0
        state.errorMessage = ""
      })
      .addCase(fetchTdsReceivable.rejected, (state, action) => {
        state.status = ApiStatus.FAILED
        state.errorMessage = action.payload?.message || action.error.message || "Failed to fetch TDS receivable data"
        state.invoices = []
        state.summary = null
        state.total = 0

        toast.error(state.errorMessage)
      })

      // Export TDS Receivable
      .addCase(exportTdsReceivable.pending, (state) => {
        // You might want to show a loading indicator for export
      })
      .addCase(exportTdsReceivable.fulfilled, (state, action) => {
        // Handle successful export - maybe trigger download
        toast.success("TDS Receivable data exported successfully")
      })
      .addCase(exportTdsReceivable.rejected, (state, action) => {
        const errorMessage = action.payload?.message || action.error.message || "Failed to export TDS receivable data"
        toast.error(errorMessage)
      })
  },
})

export const { setFilters, clearFilters, setCurrentPage, setLimit, resetState } = tdsReceivableSlice.actions

export default tdsReceivableSlice.reducer
